import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  createTemplate,
  deleteTemplate,
  fetchTemplate,
  listEventCatalog,
  listTemplates,
  previewTemplate,
  seedRecommendedTemplates,
  sendTemplateTest,
  submitTemplateToMeta,
  syncTemplate,
  syncTemplates,
  templatesQueryFrom,
  updateTemplate,
} from '@/api/templates';
import { queryKeys } from '@/lib/query-keys';
import type {
  CreateTemplateRequest,
  PlantillaFiltros,
  PreviewTemplateRequest,
  SeedTemplatesRequest,
  SendTestRequest,
  UpdateTemplateRequest,
  WhatsappTemplate,
} from '@/types/templates';

/**
 * Plantillas de WhatsApp del negocio activo (módulo 8).
 *
 * Leerlas lo puede hacer cualquier miembro (el catálogo, el detalle y la previsualización también);
 * crear, editar, borrar, sembrar las recomendadas, enviar a Meta, sincronizar y mandar una prueba son
 * solo ADMIN (`requireRole('ADMIN')`, 403 si alguien invoca el endpoint a mano).
 *
 * Cinco detalles del contrato que se ven acá:
 *  - el listado tiene filtros REALES (`evento`, `categoria`, `estado`) y devuelve un array pelado, así
 *    que los filtros van a la query key con `keepPreviousData` para que la tabla no parpadee;
 *  - las escrituras invalidan el módulo COMPLETO (`queryKeys.templates.all`): sembrar y sincronizar
 *    cambian muchas filas de una vez y el catálogo de eventos (`/eventos`) trae, por evento, el estado
 *    de la plantilla del negocio;
 *  - «sembrar recomendadas» y «sincronizar todas» devuelven el catálogo entero y SIEMBRAN la caché del
 *    listado sin filtros con esos `items` (el endpoint no pagina, así que son todas las filas): el admin
 *    ve el resultado sin esperar un segundo viaje;
 *  - `preview` es una MUTATION, no una query: es un `POST` con body y no se cachea nada (el diálogo
 *    pinta siempre lo último que pidió el admin);
 *  - la prueba es un envío REAL por WhatsApp y no toca ninguna fila del módulo, así que no invalida
 *    nada (si el admin pasó un `conversationId`, el mensaje aparece en el inbox, que tiene su propia
 *    caché: eso es de la pantalla de Conversaciones).
 */

/** `GET /api/tenants/:tenantId/whatsapp-templates` — todas las plantillas (con sus filtros). */
export function useTemplates(tenantId: string | null, filtros: PlantillaFiltros = {}) {
  // Los filtros que viajan al backend son los mismos que forman la query key.
  const query = templatesQueryFrom(filtros);

  return useQuery({
    queryKey: queryKeys.templates.list(tenantId ?? '', query),
    queryFn: () => listTemplates(tenantId ?? '', filtros),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });
}

/**
 * `GET /api/tenants/:tenantId/whatsapp-templates/:templateId` — una plantilla.
 *
 * La tabla no lo necesita (el listado trae la fila entera), así que la pantalla no lo usa hoy: queda
 * para el detalle de una fila y para que el smoke compruebe el aislamiento entre negocios. Con
 * `templateId` vacío la query no corre.
 */
export function useTemplate(tenantId: string | null, templateId: string | null) {
  return useQuery({
    queryKey: queryKeys.templates.detail(tenantId ?? '', templateId ?? ''),
    queryFn: () => fetchTemplate(tenantId ?? '', templateId ?? ''),
    enabled: Boolean(tenantId && templateId),
  });
}

/**
 * `GET /api/tenants/:tenantId/whatsapp-templates/eventos` — catálogo de eventos del sistema.
 *
 * Es la guía del onboarding: qué plantilla recomienda el sistema para cada evento, con qué campos, y si
 * el negocio ya la tiene. Se cachea bastante (`enabled` con tenant) porque solo cambia con las
 * escrituras del módulo, que lo invalidan.
 */
export function useTemplateCatalog(tenantId: string | null) {
  return useQuery({
    queryKey: queryKeys.templates.eventos(tenantId ?? ''),
    queryFn: () => listEventCatalog(tenantId ?? ''),
    enabled: Boolean(tenantId),
  });
}

function invalidateTemplates(queryClient: QueryClient, tenantId: string | null): void {
  if (tenantId) queryClient.invalidateQueries({ queryKey: queryKeys.templates.all(tenantId) });
}

/** Escribe el catálogo completo en la caché del listado sin filtros (lo que devuelven sembrar/sincronizar). */
function seedListCache(
  queryClient: QueryClient,
  tenantId: string | null,
  items: WhatsappTemplate[],
): void {
  if (!tenantId) return;
  queryClient.setQueryData(queryKeys.templates.list(tenantId, {}), items);
}

/** `POST /api/tenants/:tenantId/whatsapp-templates` (solo ADMIN) — responde 201 con la fila creada. */
export function useCreateTemplate(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateTemplateRequest) => createTemplate(tenantId ?? '', payload),
    onSuccess: (plantilla) => {
      invalidateTemplates(queryClient, tenantId);
      if (tenantId) {
        queryClient.setQueryData(queryKeys.templates.detail(tenantId, plantilla.id), plantilla);
      }
    },
  });
}

/**
 * `PATCH /api/tenants/:tenantId/whatsapp-templates/:templateId` (solo ADMIN).
 *
 * Sirve para la fila completa (solo si sigue en `BORRADOR`), para el `PATCH` del mapeo y para el
 * interruptor `activo` de la tabla: en los tres casos la respuesta es la fila actualizada.
 */
export function useUpdateTemplate(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      payload,
    }: {
      templateId: string;
      payload: UpdateTemplateRequest;
    }) => updateTemplate(tenantId ?? '', templateId, payload),
    onSuccess: (plantilla) => {
      invalidateTemplates(queryClient, tenantId);
      if (tenantId) {
        queryClient.setQueryData(queryKeys.templates.detail(tenantId, plantilla.id), plantilla);
      }
    },
  });
}

/** `DELETE /api/tenants/:tenantId/whatsapp-templates/:templateId` (solo ADMIN). */
export function useDeleteTemplate(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      eliminarEnMeta,
    }: {
      templateId: string;
      eliminarEnMeta: boolean;
    }) => deleteTemplate(tenantId ?? '', templateId, eliminarEnMeta),
    onSuccess: (result) => {
      invalidateTemplates(queryClient, tenantId);
      if (tenantId) {
        queryClient.removeQueries({ queryKey: queryKeys.templates.detail(tenantId, result.id) });
      }
    },
  });
}

/**
 * `POST .../:templateId/preview` — cuerpo renderizado con los valores que se manden.
 *
 * Es una mutation porque es un `POST` y porque el resultado se pinta al instante, sin caché: cada vez
 * que el admin cambia un valor quiere ver el texto nuevo.
 */
export function usePreviewTemplate(tenantId: string | null) {
  return useMutation({
    mutationFn: ({
      templateId,
      payload,
    }: {
      templateId: string;
      payload: PreviewTemplateRequest;
    }) => previewTemplate(tenantId ?? '', templateId, payload),
  });
}

/**
 * `POST .../:templateId/enviar-meta` (solo ADMIN) — la crea en el WABA para que Meta la revise.
 *
 * La fila cambia de estado y de `metaTemplateId`, así que se escribe en el detalle y se invalida el
 * módulo (el catálogo de eventos muestra el estado nuevo).
 */
export function useSubmitTemplateToMeta(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateId: string) => submitTemplateToMeta(tenantId ?? '', templateId),
    onSuccess: (plantilla) => {
      invalidateTemplates(queryClient, tenantId);
      if (tenantId) {
        queryClient.setQueryData(queryKeys.templates.detail(tenantId, plantilla.id), plantilla);
      }
    },
  });
}

/** `POST .../:templateId/sincronizar` (solo ADMIN) — estado real de ESA plantilla en el WABA. */
export function useSyncTemplate(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateId: string) => syncTemplate(tenantId ?? '', templateId),
    onSuccess: (plantilla) => {
      invalidateTemplates(queryClient, tenantId);
      if (tenantId) {
        queryClient.setQueryData(queryKeys.templates.detail(tenantId, plantilla.id), plantilla);
      }
    },
  });
}

/** `POST .../whatsapp-templates/sincronizar` (solo ADMIN) — todas de una, con una sola consulta a Meta. */
export function useSyncTemplates(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => syncTemplates(tenantId ?? ''),
    onSuccess: (result) => {
      seedListCache(queryClient, tenantId, result.items);
      invalidateTemplates(queryClient, tenantId);
    },
  });
}

/**
 * `POST .../whatsapp-templates/recomendadas` (solo ADMIN) — siembra los borradores que falten.
 *
 * Es idempotente de facto (los eventos que ya tienen plantilla vuelven en `existentes`), así que se
 * puede repetir sin miedo; la respuesta trae el catálogo entero y con eso se siembra la caché.
 */
export function useSeedTemplates(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SeedTemplatesRequest) => seedRecommendedTemplates(tenantId ?? '', payload),
    onSuccess: (result) => {
      seedListCache(queryClient, tenantId, result.items);
      invalidateTemplates(queryClient, tenantId);
    },
  });
}

/**
 * `POST .../:templateId/prueba` (solo ADMIN) — **envío REAL** por WhatsApp.
 *
 * Solo funciona con una plantilla APROBADA por Meta y con `headerImageUrl` si el encabezado es de
 * IMAGEN (el backend responde 400 con el mensaje exacto). No invalida nada: la prueba no cambia la fila
 * (el mensaje saliente, si se pasó `conversationId`, es del inbox).
 */
export function useSendTemplateTest(tenantId: string | null) {
  return useMutation({
    mutationFn: ({ templateId, payload }: { templateId: string; payload: SendTestRequest }) =>
      sendTemplateTest(tenantId ?? '', templateId, payload),
  });
}

